import { useState, useEffect, useRef } from 'react';
import { Cliente, TipoCliente } from '@/types';
import clientService from '@/services/clientService';
import Input from './Input';
import Button from './Button';

type Props = {
  tipo: TipoCliente;
  selectedClient: Cliente | null;
  onClientSelect: (client: Cliente | null) => void;
  label?: string;
};

export default function ClientSearchCard({ tipo, selectedClient, onClientSelect, label }: Props) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const defaultLabel = tipo === TipoCliente.PROPIETARIO ? 'Propietario' : 'Inquilino';

  // Helper to get client full name
  const getClientFullName = (client: Cliente): string => {
    if (client.nombre_completo) return client.nombre_completo;
    const parts = [
      client.primer_nombre,
      client.segundo_nombre,
      client.primer_apellido,
      client.segundo_apellido,
    ].filter(Boolean);
    return parts.join(' ');
  };

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await clientService.searchClients(searchTerm);
        const clients = response.data || response || [];
        // Filter by tipo
        const filtered = clients.filter((c: Cliente) =>
          c.tipo === tipo || c.tipo === 'AMBOS'
        );
        setSearchResults(filtered);
        setShowResults(true);
      } catch (error) {
        console.error('Error searching clients:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300); // Debounce

    return () => clearTimeout(timer);
  }, [searchTerm, tipo]);

  const handleSelect = (client: Cliente) => {
    onClientSelect(client);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleClear = () => {
    onClientSelect(null);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  return (
    <div ref={containerRef} className="border rounded-lg p-4 bg-white">
      <label className="block text-sm font-medium mb-2">
        {label || defaultLabel} {tipo === TipoCliente.PROPIETARIO ? '(requerido)' : '(opcional)'}
      </label>

      {selectedClient ? (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium">
                {getClientFullName(selectedClient)}
              </p>
              <p className="text-sm text-gray-600">
                {selectedClient.tipo_documento}: {selectedClient.documento}
              </p>
              {selectedClient.email && (
                <p className="text-sm text-gray-600">{selectedClient.email}</p>
              )}
              {selectedClient.telefono && (
                <p className="text-sm text-gray-600">{selectedClient.telefono}</p>
              )}
            </div>
            <button
              onClick={handleClear}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Quitar
            </button>
          </div>
        </div>
      ) : (
        <div className="relative">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Buscar por nombre, documento o email...`}
          />

          {loading && (
            <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white border rounded shadow-sm text-sm text-gray-600">
              Buscando...
            </div>
          )}

          {showResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-64 overflow-y-auto z-10">
              {searchResults.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleSelect(client)}
                  className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0"
                >
                  <p className="font-medium">
                    {getClientFullName(client)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {client.tipo_documento}: {client.documento}
                  </p>
                  {client.email && (
                    <p className="text-sm text-gray-500">{client.email}</p>
                  )}
                </button>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && searchTerm.length >= 2 && !loading && (
            <div className="absolute top-full left-0 right-0 mt-1 p-3 bg-white border rounded shadow-sm">
              <p className="text-sm text-gray-600">No se encontraron resultados</p>
              <p className="text-xs text-gray-500 mt-1">
                Prueba con otro término de búsqueda
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
